/**
 * SimpleTariffBars Component
 *
 * Simplified horizontal bar chart showing tariff comparison.
 * Used on the SharedCalculation preview page for visitors from social media.
 */

import { Stack, Group, Text, Progress, Badge, Box } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

interface TariffBar {
  name: string;
  cost: number;
  isBest?: boolean;
  isBaseline?: boolean;
}

interface SimpleTariffBarsProps {
  tariffs: TariffBar[];
  maxCost?: number;
}

export function SimpleTariffBars({ tariffs, maxCost }: SimpleTariffBarsProps) {
  // Calculate max cost for bar scaling
  const max = maxCost || Math.max(...tariffs.map(t => t.cost));

  // Sort by cost (cheapest first)
  const sortedTariffs = [...tariffs].sort((a, b) => a.cost - b.cost);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Stack gap="sm">
      {sortedTariffs.map((tariff, index) => {
        const percentage = (tariff.cost / max) * 100;
        const isCheapest = index === 0;

        return (
          <Box key={tariff.name}>
            <Group justify="space-between" mb={4}>
              <Group gap="xs">
                <Text fw={isCheapest ? 700 : 500} size="sm">
                  {tariff.name}
                </Text>
                {isCheapest && (
                  <Badge
                    size="xs"
                    color="green"
                    variant="light"
                    leftSection={<IconCheck size={10} />}
                  >
                    Najlepsza
                  </Badge>
                )}
                {tariff.isBaseline && !isCheapest && (
                  <Badge size="xs" color="gray" variant="outline">
                    Obecna
                  </Badge>
                )}
              </Group>
              <Text fw={isCheapest ? 700 : 400} size="sm" c={isCheapest ? 'green.7' : undefined}>
                {formatCurrency(tariff.cost)}
              </Text>
            </Group>
            <Progress
              value={percentage}
              color={isCheapest ? 'green' : tariff.isBaseline ? 'orange' : 'gray'}
              size="lg"
              radius="sm"
            />
          </Box>
        );
      })}
    </Stack>
  );
}
